import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DongnimeLogo } from '../components/DongnimeLogo';
import { DramaCard } from '../components/DramaCard';
import { Poster } from '../components/Poster';
import { UpdateCard } from '../components/UpdateCard';
import {
  EmptyState,
  ErrorMessage,
  LoadingGrid,
} from '../components/LoadingGrid';
import { CategoryChips } from '../components/CategoryChips';
import { HeroCarousel, type HeroItem } from '../components/HeroCarousel';
import { api } from '../lib/api-client';
import {
  categoryLabel,
  defaultCategory,
  type CategoryId,
} from '../lib/categories';
import { useCurrentUser } from '../lib/hooks';
import { useLang } from '../lib/lang';
import { useTheme } from '../lib/theme';
import type { Drama, HistoryView, UpdateEntry } from '../lib/types';

/** Jumlah judul yang dimuat per langkah di feed gabungan. */
const PAGE = 60;

/** Kartu lanjut nonton: poster + progress bar + tombol play. */
function ContinueCard({ item }: { item: HistoryView }) {
  const pct =
    item.durationSeconds && item.durationSeconds > 0
      ? Math.min(100, Math.round((item.positionSeconds / item.durationSeconds) * 100))
      : 0;
  return (
    <Link
      to={`/player/${item.platform}/${item.externalDramaId}/${item.externalEpisodeId}`}
      className="group block"
    >
      <div className="relative h-[98px] overflow-hidden rounded-xl bg-ink-850">
        <Poster src={item.dramaPoster} title={item.dramaTitle} />
        <div className="absolute inset-0 grid place-items-center bg-black/25">
          <span className="grid h-[30px] w-[30px] place-items-center rounded-full border-[1.5px] border-brand-400/85 bg-black/55">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="#F9D76E">
              <path d="M8 5.1v13.8c0 .8.9 1.3 1.6.9l10.7-6.9a1 1 0 0 0 0-1.7L9.6 4.2A1 1 0 0 0 8 5.1z" />
            </svg>
          </span>
        </div>
        <div className="absolute inset-x-1.5 bottom-1.5 h-[3px] rounded-sm bg-white/25">
          <div
            className="h-full rounded-sm bg-brand-500"
            style={{ width: `${Math.max(pct, 4)}%` }}
          />
        </div>
      </div>
      <h3 className="mt-1.5 line-clamp-1 text-[11px] font-semibold">
        {item.dramaTitle}
      </h3>
      <p className="text-[10px] text-neutral-500">Ep {item.episodeNumber}</p>
    </Link>
  );
}

function SectionHead({ title, to }: { title: string; to?: string }) {
  return (
    <div className="mb-3 flex items-baseline justify-between">
      <h2 className="font-display text-base font-bold">{title}</h2>
      {to && (
        <Link to={to} className="text-xs font-semibold text-brand-500">
          Lihat Semua
        </Link>
      )}
    </div>
  );
}

export function HomePage() {
  const [category, setCategory] = useState<CategoryId>(defaultCategory);
  const lang = useLang();
  const theme = useTheme();

  // Feed gabungan dipotong di server; "Muat lebih banyak" menaikkan angkanya.
  // Daftar penuh sudah di-cache backend, jadi permintaan berikutnya instan.
  const [limit, setLimit] = useState(PAGE);

  // Kategori "Terbaru" tidak memanggil katalog platform: isinya justru
  // hasil scraper (snapshot yang episodenya baru bertambah), terbaru dulu.
  const isTerbaru = category === 'terbaru';

  const homeQuery = useQuery({
    queryKey: ['home', 'category', category, lang, limit],
    queryFn: () => api.categoryHome(category, lang, limit),
    enabled: !isTerbaru,
    placeholderData: keepPreviousData,
  });

  // "Baru Diupdate" ikut kategori yang dipilih supaya seluruh beranda
  // bicara tentang satu jenis tontonan. Di kategori Terbaru, daftar ini
  // SEKALIGUS menjadi isi utama halaman.
  const updatesQuery = useQuery({
    queryKey: ['updates', 'category', category, isTerbaru ? limit : 12],
    queryFn: () =>
      isTerbaru
        ? api.updates({ limit })
        : api.updates({ limit: 12, category }),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Sorotan: judul populer yang sudah diverifikasi backend benar-benar
  // bisa diputar. Hanya untuk kategori Populer — kategori lain memakai
  // judul teratas feed-nya sendiri.
  const spotlightQuery = useQuery({
    queryKey: ['spotlight', lang],
    queryFn: () => api.spotlight(lang, 6),
    enabled: category === 'populer',
    staleTime: 30 * 60 * 1000,
  });

  const meQuery = useCurrentUser();
  const me = meQuery.data?.user ?? null;

  const historyQuery = useQuery({
    queryKey: ['history'],
    queryFn: () => api.history(),
    enabled: me !== null,
  });
  const continueItems = (historyQuery.data?.items ?? []).slice(0, 3);

  /** Entri update → bentuk Drama, supaya bisa memakai kartu yang sama. */
  const asDrama = (u: UpdateEntry): Drama => ({
    id: u.dramaId,
    platform: u.platform,
    title: u.title,
    author: null,
    cover: u.cover,
    synopsis: null,
    status: null,
    genres: [],
    tags: [],
    totalEpisodes: u.totalEpisodes,
    views: null,
  });

  const updateItems = updatesQuery.data?.items ?? [];
  const items = isTerbaru
    ? updateItems.map(asDrama)
    : (homeQuery.data?.items ?? []);

  const spotlightItems = spotlightQuery.data?.items ?? [];
  const useSpotlight = category === 'populer' && spotlightItems.length > 0;
  const heroItems: HeroItem[] = useSpotlight
    ? spotlightItems.map((u) => ({
        platform: u.platform,
        id: u.dramaId,
        title: u.title,
        cover: u.cover,
        totalEpisodes: u.totalEpisodes,
      }))
    : items.slice(0, 5).map((d) => ({
        platform: d.platform,
        id: d.id,
        title: d.title,
        cover: d.cover,
        totalEpisodes: d.totalEpisodes,
        status: d.status,
      }));
  // Judul yang sudah tampil di carousel tidak diulang di grid — kecuali
  // carousel memakai daftar sorotan, yang sumbernya memang berbeda.
  const rest = items.slice(useSpotlight ? 0 : heroItems.length);

  const feedPending = isTerbaru ? updatesQuery.isPending : homeQuery.isPending;
  const feedError = isTerbaru ? updatesQuery.error : homeQuery.error;
  const refetchFeed = () => {
    void (isTerbaru ? updatesQuery.refetch() : homeQuery.refetch());
  };

  return (
    <div className="mx-auto max-w-6xl px-4 safe-top">
      {/* Header */}
      <header className="flex items-center justify-between py-3">
        <DongnimeLogo variant="horizontal" theme={theme} height={30} />
        <div className="flex items-center">
          <Link
            to="/search"
            aria-label="Cari"
            className="grid h-11 w-11 place-items-center"
          >
            <svg className="text-neutral-300" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <circle cx="11" cy="11" r="7.5" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
          </Link>
          <Link
            to="/jadwal"
            aria-label="Jadwal rilis"
            className="relative grid h-11 w-11 place-items-center"
          >
            <svg className="text-neutral-300" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8.5a6 6 0 1 0-12 0c0 6.2-2.4 7.5-2.4 7.5h16.8S18 14.7 18 8.5z" />
              <path d="M13.7 20a2 2 0 0 1-3.4 0" />
            </svg>
            {(updatesQuery.data?.items.length ?? 0) > 0 && (
              <span className="absolute right-2.5 top-2.5 h-[7px] w-[7px] rounded-full bg-brand-500 ring-2 ring-ink-950" />
            )}
          </Link>
        </div>
      </header>

      {/* Kategori tontonan */}
      <CategoryChips
        selected={category}
        onSelect={(c) => {
          setCategory(c);
          setLimit(PAGE); // ganti kategori → mulai lagi dari halaman pertama
        }}
      />

      {/* Sorotan — geser manual atau berpindah sendiri tiap 5 detik */}
      {heroItems.length > 0 && (
        <section className="mt-4">
          <HeroCarousel items={heroItems} />
        </section>
      )}

      {/* Lanjutkan menonton */}
      {continueItems.length > 0 && (
        <section className="mt-6">
          <SectionHead title="Lanjutkan Menonton" to="/history" />
          <div className="grid grid-cols-3 gap-3">
            {continueItems.map((h) => (
              <ContinueCard key={h.id} item={h} />
            ))}
          </div>
        </section>
      )}

      {/* Baru diupdate — di kategori Terbaru sudah jadi isi utama di bawah */}
      {!isTerbaru && updateItems.length > 0 && (
        <section className="mt-6">
          <SectionHead title="Baru Diupdate" to="/jadwal" />
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {updateItems.map((u) => (
              <UpdateCard key={`${u.platform}-${u.dramaId}`} update={u} />
            ))}
          </div>
        </section>
      )}

      {/* Grid utama */}
      <section className="mt-6 pb-4">
        <SectionHead
          title={
            category === 'populer'
              ? 'Rekomendasi Untukmu'
              : isTerbaru
                ? 'Baru Diupdate'
                : categoryLabel(category)
          }
        />

        {feedPending ? (
          <LoadingGrid />
        ) : feedError ? (
          <ErrorMessage
            title="Gagal memuat"
            message={feedError.message}
            onRetry={refetchFeed}
          />
        ) : rest.length === 0 ? (
          <EmptyState
            message={`Belum ada judul di kategori ${categoryLabel(category)}.`}
          />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {rest.map((d) => (
                <DramaCard
                  key={`${d.platform}-${d.id}`}
                  drama={d}
                  showPlatform
                />
              ))}
            </div>

            {(isTerbaru
              ? updateItems.length >= limit
              : (homeQuery.data?.hasMore ?? false)) && (
              <button
                type="button"
                onClick={() => setLimit((n) => n + PAGE)}
                disabled={homeQuery.isFetching || updatesQuery.isFetching}
                className="mt-4 h-12 w-full rounded-xl border border-brand-500/35 text-sm font-bold text-brand-400 disabled:opacity-50"
              >
                {homeQuery.isFetching || updatesQuery.isFetching
                  ? 'Memuat…'
                  : 'Muat lebih banyak'}
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
